import { useQuery } from '@tanstack/react-query'
import { useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { type CasesSort } from '@/pages/_app/types'
import { useCaseRowSelection } from '@/pages/_app/ui/cases/hooks/use-case-row-selection'
import { scrollAppPageToTop } from '@/pages/_app/utils'

import { CASE_ARCHIVE_STATUS_MAP } from '@/lib/queries/business/constants'
import { getSearchCasesQuery } from '@/lib/queries/business/v4/case/search/queries'
import { type ValueOf } from '@/lib/utils/type'

import { type ApplicantCasesProps } from './applicant-cases'
import { CASE_TABLE_TAB_MAP } from './constants'

export type UseApplicantCasesParams = Pick<ApplicantCasesProps, 'currentCategoryTab' | 'isEoir'>

export const useApplicantCases = ({ currentCategoryTab, isEoir }: UseApplicantCasesParams) => {
  const navigate = useNavigate({
    from: '/applicants/$applicantId',
  })
  const { applicantId } = useParams({
    from: '/_app/applicants/$applicantId',
  })
  const { caseNumber } = useParams({
    strict: false,
  })
  const searchParams = useSearch({
    from: '/_app/applicants/$applicantId',
  })
  const { sortKey, sortOrder } = searchParams

  const [currentCaseTableTab, setCurrentCaseTableTab] = useState<ValueOf<typeof CASE_TABLE_TAB_MAP>>(
    CASE_TABLE_TAB_MAP.ACTIVE
  )

  const { data: dataListCase, isLoading: isLoadingCases } = useQuery(
    getSearchCasesQuery({
      isEoir,
      path_id: Number(applicantId),
      sort: sortKey,
      asc: sortOrder === 'asc',
      archiveStatus:
        currentCaseTableTab === CASE_TABLE_TAB_MAP.ACTIVE
          ? CASE_ARCHIVE_STATUS_MAP.active
          : CASE_ARCHIVE_STATUS_MAP.archived,
    })
  )

  /**
   * Filter cases based on the selected tab.
   */
  const filteredCases = useMemo(() => {
    if (currentCategoryTab === applicantId) {
      return dataListCase?.cases
    }
    return dataListCase?.cases?.filter((caseItem) => caseItem.path_id === Number(currentCategoryTab))
  }, [dataListCase, currentCategoryTab, applicantId])

  const caseIds = useMemo(() => filteredCases?.map((x) => x.case_id) ?? [], [filteredCases])

  const { selection, isEveryRowSelected, handleToggleAllRowSelection, handleToggleCaseRowSelection } =
    useCaseRowSelection({ ids: caseIds })

  const handleSortChange = useCallback(
    async (sort: CasesSort) => {
      await navigate({
        search: {
          ...searchParams,
          sortKey: sort.key,
          sortOrder: sort.order,
        },
      })
      scrollAppPageToTop()
    },
    [navigate, searchParams]
  )

  const handleChangeCaseTableTab = async (value: string) => {
    await navigate({
      to: '/applicants/$applicantId',
    })
    setCurrentCaseTableTab(value as ValueOf<typeof CASE_TABLE_TAB_MAP>)
  }

  useEffect(
    function changeCaseTableTabWhenCaseNumberSpecified() {
      if (!caseNumber || !dataListCase) {
        return
      }

      const isInView = dataListCase.cases?.some(({ case_number }) => caseNumber === case_number)
      if (!isInView) {
        setCurrentCaseTableTab(CASE_TABLE_TAB_MAP.INACTIVE)
      }
    },
    [caseNumber, dataListCase]
  )

  return {
    filteredCases,
    selection,
    sortKey,
    sortOrder,
    currentCaseTableTab,
    isEveryRowSelected,
    isLoadingCases,
    handleToggleCaseRowSelection,
    handleToggleAllRowSelection,
    handleSortChange,
    handleChangeCaseTableTab,
  }
}
